import axios from "axios";
import { ElMessage } from "element-plus";

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 10000,
});

// --- Refresh Token 관리 ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  // 이미 로그인 페이지면 중복 이동 방지
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// 요청 인터셉터 (요청 전 처리)
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 안전장치: /api/api/ 이중 프리픽스 감지 및 수정
    if (config.url && config.url.startsWith('/api/api/')) {
      console.warn(`[axios] 이중 /api/ 프리픽스 감지: ${config.url}`);
      config.url = config.url.replace(/^\/api\/api\//, '/api/');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 (401 시 자동 refresh)
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 에러 메시지 파싱
    if (error.response && error.response.data) {
      const parsedErrors = parseErrorMessages(error.response.data);
      error.customErrors = parsedErrors;
    }

    if (error.response) {
      const status = error.response.status;

      // 401: refresh token으로 자동 갱신 시도
      if (status === 401 && !originalRequest._retry) {
        // refresh 요청 자체가 실패한 경우 → 로그아웃
        if (originalRequest.url === "token/refresh/") {
          forceLogout();
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
          forceLogout();
          return Promise.reject(error);
        }

        // 이미 refresh 진행 중이면 큐에 대기
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          });
        }

        isRefreshing = true;

        try {
          const response = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/token/refresh/`,
            { refresh: refreshToken }
          );
          const newAccessToken = response.data.access;
          localStorage.setItem("access_token", newAccessToken);

          // 새 refresh token이 있으면 저장 (ROTATE_REFRESH_TOKENS=True 시)
          if (response.data.refresh) {
            localStorage.setItem("refresh_token", response.data.refresh);
          }

          processQueue(null, newAccessToken);

          // 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          ElMessage.error("세션이 만료되었습니다. 다시 로그인하세요.");
          forceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // 그 외 상태 코드
      switch (status) {
        case 400:
          if (Array.isArray(error.customErrors)) {
            error.customErrors.forEach((msg) => {
              ElMessage.error(msg);
            });
          }
          break;
        case 403:
          ElMessage.error("권한이 없습니다.");
          break;
        case 404:
          ElMessage.error("요청한 리소스를 찾을 수 없습니다.");
          break;
        case 500:
          ElMessage.error("서버에서 오류가 발생했습니다. 나중에 다시 시도하세요.");
          break;
        default:
          if (status !== 401) {
            ElMessage.error("알 수 없는 오류가 발생했습니다.");
          }
      }
    }

    return Promise.reject(error);
  }
);


// 에러 메시지 파싱
function parseErrorMessages(errorData) {
  const messages = [];
  for (const [field, errors] of Object.entries(errorData)) {
    if (Array.isArray(errors)) {
      errors.forEach((msg) => messages.push(`${field}: ${msg}`));
    } else {
      messages.push(`${field}: ${errors}`);
    }
  }
  return messages;
}


export const fetchSchema = async (modelName) => {
  try{
    const response = await axiosInstance.get(`/schema/${modelName}/`);
    return response.data;
  }catch(error){
    console.error('Error fetching model schema:', error);
    throw error;
  }
};

export default axiosInstance;
